import { FC, useMemo } from "react";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { useNavigate } from "@/components/_molecules/navigation-controls/use-navigate";
import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useGetTeam } from "@/domain/team/hooks/api/use-get-team";
import { useTeamJoin } from "@/domain/team/hooks/api/use-team-join";
import { useTeamMembershipList } from "@/domain/team/hooks/api/use-team-membership-list";
import { useWorkspaceById } from "@/domain/workspace/hooks/api/use-workspace-by-id";
import { extractGraphQLErrorInfo } from "@/lib/extract-graphql-error-info";
import { CheckIcon } from "@heroicons/react/24/solid";

import { TeamIcon } from "./team-icon";

interface TeamBrowseListItemProps {
  workspaceId: string;
  teamId: string;
  userId: string;
}

export const TeamBrowseListItem: FC<TeamBrowseListItemProps> = ({ workspaceId, teamId, userId }) => {
  const navigate = useNavigate();
  const { t } = useDomainTranslation("team");
  const { isEnabled: isPermissionV2Enabled } = useFeatureFlagSimple({ id: "enable-permission-v2" });

  const { workspace } = useWorkspaceById({ id: workspaceId });
  const { team } = useGetTeam({ id: teamId });
  const { teamMembershipList, getTeamMembershipForUser } = useTeamMembershipList({ teamId: teamId });

  const { joinTeam, queryResult } = useTeamJoin({
    workspace: { id: workspaceId },
    team: { id: teamId },
    onCompleted: handleOnComplete,
    onError: handleOnError,
  });

  const loading = useMemo(() => {
    return queryResult.isPending;
  }, [queryResult]);

  const teamMembershipForUser = useMemo(() => {
    return getTeamMembershipForUser(userId);
  }, [getTeamMembershipForUser, userId]);

  function handleOnComplete() {
    toast.success(t("browse.joinedTitle"), { description: t("browse.joinedDescription") });

    if (!workspace) {
      return;
    }

    if (!team) {
      return;
    }

    navigate(`/${workspace.workspaceUrl}/teams/${team.id}`, {
      replace: true,
    });
  }

  function handleOnError(error: Error) {
    toast.error(t("browse.errorTitle"), { description: extractGraphQLErrorInfo(error).userMessage });
  }

  const handleJoinTeam = () => {
    if (loading) {
      return;
    }

    joinTeam({
      teamId,
    });
  };

  if (!team) {
    return <></>;
  }

  return (
    <div
      key={team.id}
      className="flex cursor-pointer select-none flex-col items-start rounded-md px-2 py-2 text-sm hover:bg-muted"
    >
      <div className="flex w-full flex-row items-center gap-1">
        <TeamIcon team={team} />
        <span className="pointer-events-none relative flex h-8 cursor-none items-center overflow-hidden text-ellipsis rounded-sm pb-0 pl-1.5 pr-0.5 pt-0 hover:bg-accent hover:text-accent-foreground">
          {team.name}
        </span>
        <div className="grow"></div>
        <div className="pr-3">
          {isPermissionV2Enabled && (
            <>
              {!teamMembershipForUser && (
                <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                  {t("browse.join")}
                </Button>
              )}
              {teamMembershipForUser && teamMembershipForUser.status === "LEFT" && (
                <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                  {t("browse.join")}
                </Button>
              )}
              {teamMembershipForUser && teamMembershipForUser.status === "PENDING" && (
                <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                  {t("browse.acceptInvite")}
                </Button>
              )}
            </>
          )}
          {!isPermissionV2Enabled && (
            <>
              {team.visibility === "PUBLIC" && (
                <>
                  {!teamMembershipForUser && (
                    <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                      {t("browse.join")}
                    </Button>
                  )}
                  {teamMembershipForUser && teamMembershipForUser.status === "LEFT" && (
                    <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                      {t("browse.join")}
                    </Button>
                  )}
                  {teamMembershipForUser && teamMembershipForUser.status === "PENDING" && (
                    <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                      {t("browse.acceptInvite")}
                    </Button>
                  )}
                </>
              )}
              {team.visibility === "PRIVATE" && (
                <>
                  {teamMembershipForUser && teamMembershipForUser.status === "LEFT" && (
                    <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                      {t("browse.join")}
                    </Button>
                  )}
                  {teamMembershipForUser && teamMembershipForUser.status === "PENDING" && (
                    <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                      {t("browse.acceptInvite")}
                    </Button>
                  )}
                </>
              )}
              {team.visibility === "HIDDEN" && (
                <>
                  {teamMembershipForUser && teamMembershipForUser.status === "LEFT" && (
                    <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                      {t("browse.join")}
                    </Button>
                  )}
                  {teamMembershipForUser && teamMembershipForUser.status === "PENDING" && (
                    <Button variant="outline" size="xs" className="" onClick={handleJoinTeam}>
                      {t("browse.acceptInvite")}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-row items-center gap-2">
        {teamMembershipForUser && (
          <>
            {teamMembershipForUser.status === "ACTIVE" && (
              <>
                <div className="flex flex-row items-center gap-0.5 text-xxs text-green-600">
                  <CheckIcon className="h-2.5 w-2.5" />
                  {teamMembershipForUser.role === "ADMIN" && <>{t("browse.administrator")}</>}
                  {teamMembershipForUser.role !== "ADMIN" && <>{t("browse.memberRole")}</>}
                </div>
              </>
            )}

            {teamMembershipForUser.status === "PENDING" && (
              <>
                <div className="flex flex-row items-center gap-0.5 text-xxs text-muted-foreground">
                  {t("browse.pendingInvite")}
                </div>
              </>
            )}

            {teamMembershipForUser.status === "LEFT" && (
              <>
                <div className="flex flex-row items-center gap-0.5 text-xxs text-muted-foreground">{t("browse.leftTeam")}</div>
              </>
            )}

            <div className="text-xxs text-muted-foreground">
              <span>{"·"}</span>
            </div>
          </>
        )}

        {teamMembershipList.length === 1 && (
          <div className="text-xxs text-muted-foreground">{t("management.memberCount", { count: teamMembershipList.length })}</div>
        )}
        {teamMembershipList.length !== 1 && (
          <div className="text-xxs text-muted-foreground">{t("management.memberCountPlural", { count: teamMembershipList.length })}</div>
        )}
        <FeatureFlagToggle
          flag="enable-permission-v2"
          fallback={
            <>
              <div className="text-xxs text-muted-foreground">
                <span>{"·"}</span>
              </div>
            </>
          }
        >
          <></>
        </FeatureFlagToggle>
        <FeatureFlagToggle
          flag="enable-permission-v2"
          fallback={
            <div className="text-xxs text-muted-foreground">
              {team.visibility === "PUBLIC" && <>{t("browse.publicTeam")}</>}
              {team.visibility === "PRIVATE" && (
                <>
                  {teamMembershipForUser && teamMembershipForUser.status !== "SUSPENDED" && <>{t("browse.privateTeam")}</>}

                  {(!teamMembershipForUser || teamMembershipForUser?.status === "SUSPENDED") && (
                    <>{t("browse.privateTeamAskAdmin")}</>
                  )}
                </>
              )}
              {team.visibility === "HIDDEN" && <>{t("browse.hiddenTeam")}</>}
            </div>
          }
        >
          <></>
        </FeatureFlagToggle>
      </div>
    </div>
  );
};
